import { Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { Chip } from 'components/chips'
import { FedExShipmentDto } from 'dtos/fedEx'
import { MichiganOutgoingTitleDto } from 'dtos/michiganOutgoingTitles'
import { formatDate, formatDateMoment } from 'utils'

interface MichiganOutgoingTitlesDetailsCardProps {
  fedExShipment: FedExShipmentDto | undefined
  michiganOutgoingTitle: MichiganOutgoingTitleDto
}

export default function MichiganOutgoingTitlesDetailsCard({
  children,
  fedExShipment,
  michiganOutgoingTitle
}: React.PropsWithChildren<MichiganOutgoingTitlesDetailsCardProps>) {
  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems='flex-start'>
          <Grid item xs={12} container alignContent='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto' container spacing={1}>
              <Grid item>
                <Typography fontWeight='bold' variant='h5'>
                  Michigan Outgoing Titles - Group Details
                </Typography>
              </Grid>

              <Grid item>
                <Chip variant={michiganOutgoingTitle.isLocked ? 'red' : 'green'}>{michiganOutgoingTitle.isLocked ? 'Locked' : 'Unlocked'}</Chip>
              </Grid>
            </Grid>

            <Grid item xs={12} sm='auto' container>
              {children}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField label='Count' value={michiganOutgoingTitle.vehicles?.length} />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField
              label='Tracking #'
              value={fedExShipment?.trackingNumber}
              onClick={
                fedExShipment?.trackingNumber ? () => window.open(`https://fedex.com/wtrk/track/?trknbr=${fedExShipment.trackingNumber}`, '_blank') : undefined
              }
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField label='Group Shipped' value={fedExShipment?.shipDateTime ? formatDateMoment(fedExShipment.shipDateTime) : undefined} />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField label='Group Delivered' value={fedExShipment?.deliveryDateTime ? formatDateMoment(fedExShipment.deliveryDateTime) : undefined} />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField
              label='Created'
              value={
                michiganOutgoingTitle.createdDateTime
                  ? `${formatDate(michiganOutgoingTitle.createdDateTime)} by ${
                      michiganOutgoingTitle.createdByPerson
                        ? `${michiganOutgoingTitle.createdByPerson.firstName} ${michiganOutgoingTitle.createdByPerson.lastName || ''}`.trim()
                        : '-'
                    }`
                  : '-'
              }
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <LabelField
              label='Modified'
              value={
                michiganOutgoingTitle.updatedDateTime
                  ? `${formatDate(michiganOutgoingTitle.updatedDateTime)} by ${
                      michiganOutgoingTitle.updatedByPerson
                        ? `${michiganOutgoingTitle.updatedByPerson.firstName} ${michiganOutgoingTitle.updatedByPerson.lastName || ''}`.trim()
                        : '-'
                    }`
                  : '-'
              }
            />
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Group Locked Chip:**

Displays when Group is marked as locked.

**Back Button:**

Clicking this button navigates the user to the previous screen.

**Generate Outgoing Titles Group FedEx Label Button:**

Clicking this button generates the  Outgoing Titles Group FedEx Label PDF.

This button is enabled only when the Group is locked.

**Lock/Unlock Bundle Button:**

Lock Group Button: Clicking this button locks the bundle and changes the button text to "Unlock Group."

An Outgoing Titles Group can only be locked if it contains at least one vehicle, and all FedEx Outbound Label fields are populated.

Unlock Bundle Button: Clicking this button unlocks the Group.

**Delete bundle Button:**

Clicking this button deletes the Group.

An Outgoing Titles Group can only be deleted if it is not locked.

**Count:**

Displays the total number of vehicles in the group.

**Tracking #:**

Reads from Michigan Outgoing Titles – Group Details.

This is the FedEx tracking number for the Outgoing Titles Group.

The tracking number is a clickable link that opens the FedEx website.

**Group Shipped:**

Reads from Michigan Outgoing Titles – Group Details.

Indicates that the FedEx API reports the group is in FedEx possession.

If no value exists, a "-" is displayed.

**Group Delivered:**

Reads from Michigan Outgoing Titles – Group Details.

Indicates that the FedEx API reports a delivered date for this group’s tracking number.

If no value exists, a "-" is displayed.

**Created:**

Displays the date and user who created the Group.

**Modified:**

Displays the date and user who most recently modified the Group.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
